import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Checkbox, CheckboxProps, Dimmer, Grid, List, Loader,
  Form, Label, Modal, Segment, Sidebar, Header, Button, Icon
} from "semantic-ui-react";
import { ReactSVG } from "react-svg";
import { cloneDeep } from "lodash";
import Swal from 'sweetalert2';
import _ from 'lodash';
import { useCookies } from "react-cookie";
import { ConstituentDataViewModel } from '../../types/ConstituentDTO';
import DataGrid, { Column } from '../Common/SharedComponents/DataGrid';
import DataGridFilters from '../Common/SharedComponents/DataGridFilters';
import { Aggregation, DynamicSearchResponse, FilterData } from '../../types/filterDataGridDTO';
import DocumentAdd from "../../assets/images/document-add.svg";
import UserInfo from '../Common/SharedComponents/UserInfo';
import AppContext from '../../AppContext';
import axiosInstance from '../../helpers/axiosInstance';
import SwitchTheme from '../Common/SharedComponents/SwitchTheme';

const Constituents: React.FC = () => {
  const [cookies, setCookies] = useCookies(['show_constituents_left_nav']);
  const navigate = useNavigate();
  const { themeVersion, userInfo } = useContext(AppContext);
  const [visible, setVisible] = useState((cookies['show_constituents_left_nav'] === undefined || cookies['show_constituents_left_nav'] === 'true') ? true : false);

  const [responseDataModel, setResponseDataModel] = useState<DynamicSearchResponse<ConstituentDataViewModel> | null>(null);
  const [Aggregates, setAggregates] = useState<Aggregation[] | null>(null);
  const [filters, setFilters] = useState<Record<string, FilterData> | null>(null);

  const [mergeIds, setMergeIds] = useState<number[]>([]);
  const [masterRecord, setMasterRecord] = useState<any>({});
  const [tmpMergeRecords, setTmpMergeRecords] = useState<any>([]);
  const [mergeRecords, setMergeRecords] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedMergeFields, setSelectedMergeFields] = useState<any>({});
  const [selectedMergeIndex, setSelectedMergeIndex] = useState<number>();
  const [isMergeModalOpen, setIsMergeModalOpen] = useState<boolean>(false);

  const buildBase64String = (constituentId: number, constituentName: string) => {
    let base64String = '';
    try {
      const filter = {
        constituent: {
          ids: [constituentId],
          isFocus: false,
          names: [constituentName]
        },
      }
      var jsonString = JSON.stringify(filter);

      // Encode JSON string as Base64
      base64String = btoa(jsonString);
    }
    catch (error) {
      console.log(error);
    }
    return base64String;
  }

  const handleMergeCheckboxChange = (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps, row: ConstituentDataViewModel) => {
    const tmpMergeIds = cloneDeep(mergeIds);
    const tmpMergeRecords1 = cloneDeep(tmpMergeRecords);
    if (tmpMergeRecords1.length > 0 && _.findIndex(tmpMergeRecords1, { office: row.office }) === -1) {
      alert('Merge constituents allowed within an office');
    } else {
      if (data.checked) {
        tmpMergeIds.push(row.constituentId);
        tmpMergeRecords1.push(row);
      } else {
        const mergeIdIndex = tmpMergeIds.findIndex((mergeId: number) => mergeId === row.constituentId);
        tmpMergeIds.splice(mergeIdIndex, 1);
        const mergeRecordIndex = _.findIndex(tmpMergeRecords, { constituentId: row.constituentId });
        tmpMergeRecords1.splice(mergeRecordIndex, 1);
      }
      if (responseDataModel && tmpMergeIds.length <= responseDataModel.maxMergeRecords) {
        setMergeIds(tmpMergeIds);
        setTmpMergeRecords(tmpMergeRecords1);
      } else {
        alert('Reached maximum allowed constituents to merge');
      }
    }
  };

  const handleRadioButtonChange = (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps, mergeIndex: number, mergeField: string, mergeValue: any) => {
    if (selectedMergeIndex !== undefined && mergeIndex !== selectedMergeIndex) {
      setSelectedMergeIndex(undefined);
    }
    setSelectedMergeFields({ ...selectedMergeFields, [mergeField]: mergeIndex });
    setMasterRecord({ ...masterRecord, [mergeField]: mergeValue });
  };

  const handleSelectAllRadioButtonChange = (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps, mergeIndex: number) => {
    let tmpSelectedMergeFields: any = {};
    let tmpMasterRecord: any = {};
    mergeFields.map(mergeField => {
      tmpSelectedMergeFields[mergeField.value] = mergeIndex;
      tmpMasterRecord[mergeField.value] = mergeRecords[mergeIndex][mergeField.value];
      return true;
    });
    setSelectedMergeIndex(mergeIndex);
    setSelectedMergeFields(tmpSelectedMergeFields);
    setMasterRecord(tmpMasterRecord);
  };

  const closeModal = () => {
    setMergeIds([]);
    setMergeRecords([]);
    setMasterRecord({});
    setSelectedMergeIndex(undefined);
    setSelectedMergeFields({});
    setIsMergeModalOpen(false);
  }

  const mergeConstituents = async () => {
    if (mergeIds.length <= 1) {
      Swal.fire({
        text: 'Please select at least two constituents to proceed.',
        icon: 'warning',
        showCancelButton: false,
        confirmButtonText: 'Ok',
      });
      return false;
    }
    try {
      setIsLoading(true);
      setIsMergeModalOpen(true);
      await axiosInstance.get(`/Constituent/GetConstituents/${mergeIds}`).then(({ data, status }) => {
        if (status === 200) {
          setMergeRecords(data);
          setIsLoading(false);
        }
      })
        .catch((error) => {
          setIsLoading(false);
          console.log(error);
        });
    } catch (error: any) {
      setIsLoading(false);
      console.log(error);
    }
  }

  const handleMergeConstituents = async () => {
    let errors: any = {};
    const requiredFields = ['firstName', 'lastName', 'email'];
    mergeFields.map(mergeField => {
      if (requiredFields.includes(mergeField.value) && masterRecord[mergeField.value] === undefined) {
        errors[mergeField.value] = true;
      }
      return true;
    });
    if (Object.keys(errors).length === 0) {
      let payload = {
        mergeConstituentIds: mergeIds,
        newConstituent: masterRecord
      };
      payload.newConstituent.phoneTypeId = selectedMergeFields['phoneType'] !== undefined ? mergeRecords[selectedMergeFields['phoneType']].phoneTypeId : null;
      payload.newConstituent.preferredContactId = selectedMergeFields['preferredContact'] !== undefined ? mergeRecords[selectedMergeFields['preferredContact']].preferredContactId : null;
      payload.newConstituent.stateId = selectedMergeFields['state'] !== undefined ? mergeRecords[selectedMergeFields['state']].stateId : null;
      payload.newConstituent.wardId = selectedMergeFields['ward'] !== undefined ? mergeRecords[selectedMergeFields['ward']].wardId : null;
      payload.newConstituent.neighborhoodId = selectedMergeFields['neighborhood'] !== undefined ? mergeRecords[selectedMergeFields['neighborhood']].neighborhoodId : null;
      Swal.fire({
        title: 'Are you sure you want to merge?',
        text: 'This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Merge',
        cancelButtonText: 'Cancel',
      }).then((result) => {
        if (result.isConfirmed) {
          try {
            setIsLoading(true);
            axiosInstance.post('/Constituent/MergeConstituents', payload).then(({ data, status }) => {
              if (status === 200) {
                setIsLoading(false);
                Swal.fire({
                  title: 'Constituent records merged successfully',
                  icon: 'success',
                  html: `<div class="form-custom" style="text-align: left">
                        <p>Prior cases and notes are now associated with:</p>
                        <p><a href="/constituent/${data}">${masterRecord.firstName} ${masterRecord.lastName}</a></p>
                        </div>`,
                  confirmButtonText: 'Ok',
                  showConfirmButton: true,
                  showDenyButton: false,
                  allowOutsideClick: false,
                  allowEscapeKey: false
                }).then((result: any) => {
                  if (result.isConfirmed) {
                    window.location.reload();
                  }
                });
              }
            })
              .catch((error) => {
                setIsLoading(false);
                console.log(error);
              });
          } catch (error: any) {
            setIsLoading(false);
            console.log(error);
          }
        }
      });
    } else {
      alert('Please select any one record for first name, last name & email fields');
    }
  }

  let columns: Column<ConstituentDataViewModel>[] = [
    { field: 'firstName', header: "First Name", sortable: true, headerStyle: { textAlign: 'left', paddingLeft: 10 }, style: { textAlign: 'left', width: '15%', fontSize: '16px', paddingLeft: 10 }, accessor: (row: ConstituentDataViewModel) => row.firstName ? <a href={`/constituent/${row.constituentId}`}>{row.firstName}</a> : null },
    { field: 'lastName', header: 'Last Name', sortable: true, className: "wrap-text", style: { textAlign: 'left', width: '15%' }, accessor: (row: ConstituentDataViewModel) => row.lastName ? <a href={`/constituent/${row.constituentId}`}>{row.lastName}</a> : null },
    { field: 'email', header: 'Email', sortable: true, className: "wrap-text", style: { width: '30%' }, accessor: (row: ConstituentDataViewModel) => row.email ? row.email : null },
    { field: 'office', header: 'Office', sortable: true, className: "wrap-text", style: { width: '10%' }, accessor: (row: ConstituentDataViewModel) => row.office },
    { field: 'phone', header: 'Phone', sortable: true, style: { width: '10%' }, accessor: (row: ConstituentDataViewModel) => row.phone ? row.phone : null },
    {
      field: 'caseCount', header: 'Cases', sortable: true, style: { width: '10%' },
      accessor: (row: ConstituentDataViewModel) => row.caseCount ?
        <a href={`/dashboard?SearchFilters=${buildBase64String(row.constituentId, encodeURIComponent(`${row.firstName} ${row.lastName}`))}`} onClick={() => {
          localStorage.removeItem('dashboardFilter');
        }}>{row.caseCount}</a>
        : ''
    },
    {
      field: 'merge', header: 'Select', sortable: false, style: { width: '10%' },
      accessor: (row: ConstituentDataViewModel) => {
        return (
          <Checkbox
            checked={mergeIds.includes(row.constituentId) ? true : false}
            onChange={(e, data) => handleMergeCheckboxChange(e, data, row)}
            style={{
              margin: '1rem 1rem 0rem 1rem',
              fontSize: '1.6rem'
            }}
          />
        );
      }
    },
  ];

  if (userInfo?.IsAllOffices !== "True") {
    columns.splice(3, 1);
  }

  useEffect(() => {
    document.title = 'Constituents - Adjure';
  }, []);

  const handleShowFilters = (event: any) => {
    setVisible(!visible);
  };

  const buttonToggleClass = visible ? "btn-toggle btn-toggle--is-active" : "btn-toggle";

  const mergeFields = [
    {
      label: 'First Name',
      value: 'firstName',
      icon: 'user',
    },
    {
      label: 'Last Name',
      value: 'lastName',
      icon: 'user',
    },
    {
      label: 'Email Address',
      value: 'email',
      icon: 'mail',
    },
    {
      label: 'Phone Number',
      value: 'phoneNumber',
      icon: 'phone',
    },
    {
      label: 'Phone Type',
      value: 'phoneType',
      icon: 'phone',
    },
    {
      label: 'Preferred Contact',
      value: 'preferredContact',
      icon: 'phone',
    },
    {
      label: 'Address',
      value: 'address',
      icon: 'address book outline',
    },
    {
      label: 'City',
      value: 'city',
      icon: 'map pin',
    },
    {
      label: 'State',
      value: 'state',
      icon: 'map outline',
    },
    {
      label: 'Zip',
      value: 'zipCode',
      icon: 'file archive',
    },
    {
      label: 'Ward',
      value: 'ward',
      icon: 'newspaper',
    },
    {
      label: 'Neighborhood',
      value: 'neighborhood',
      icon: 'map signs',
    },
    {
      label: 'Organization',
      value: 'organization',
      icon: 'sitemap',
    },
    {
      label: 'Notes',
      value: 'notesCount',
      icon: 'file outline',
    },
    {
      label: 'Cases',
      value: 'caseCount',
      icon: 'file alternate outline',
    },
  ];
  let masterRecordCaseCount = 0;
  let masterRecordNotesCount = 0;

  return (
    <div>
      <section className="section">
        <Sidebar.Pushable as={Segment}>
          <Sidebar
            animation="push"
            icon="labeled"
            inverted
            vertical
            width="thin"
            visible={visible}
          >
            <div className="sidebar__inner">
              <div className={visible ? 'display-pin' : 'hide-pin'}>
                {(cookies['show_constituents_left_nav'] === undefined || cookies['show_constituents_left_nav'] === 'true') && (
                  <Icon name='pin' onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    setCookies('show_constituents_left_nav', 'false', { path: '/' });
                  }} />
                )}
                {cookies['show_constituents_left_nav'] === 'false' && (
                  <Icon name='pin' rotated='clockwise' onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    setCookies('show_constituents_left_nav', 'true', { path: '/' });
                  }} />
                )}
              </div>
              <nav className="nav-sidebar">
                &nbsp;
              </nav>
              {
                Aggregates && Aggregates.length > 0 &&
                <div className="filters">
                  <Header as="h2">
                    Filter by
                    <span style={{ float: 'right', fontSize: '1.4rem', cursor: 'pointer', color: '#2d76d5' }} onClick={() => window.location.reload()}>Reset</span>
                  </Header>
                  <div className="select-holder">
                    {
                      filters &&
                      <DataGridFilters<ConstituentDataViewModel>
                        aggregations={Aggregates || []}
                        setFilters={setFilters}
                        filters={filters}
                        responseDataModel={responseDataModel}
                        setResponseDataModel={setResponseDataModel}
                        key={'DataGridWithFilters'}
                      />
                    }
                  </div>
                </div>
              }
            </div>
          </Sidebar>
          <Sidebar.Pusher>
            <div className="section__head">
              <div className="section__head-content">
                <Header as="h1">Constituents</Header>
              </div>
              <div className="section__actions">
                <UserInfo />
                {userInfo?.IsAllOffices !== "True" && (
                  <Button color="violet" onClick={() => navigate('/constituent')}>
                    <Button.Content visible style={{ whiteSpace: 'nowrap' }}>
                      Create Constituent
                    </Button.Content>
                    <Button.Content visible>
                      <ReactSVG src={DocumentAdd} />
                    </Button.Content>
                  </Button>
                )}
                <SwitchTheme />
              </div>
              <Button
                basic
                className="btn-filter"
                onClick={(event: any) =>
                  handleShowFilters(event)
                }
              >
                {visible && <span> Close Filters</span>}
                {visible || <span>Open Filters</span>}
              </Button>
            </div>
            <div className="section__body">
              <div className="section__actions" style={{ float: 'right', marginTop: '6px', position: 'absolute', right: '75px' }}>
                <Button className="btn-bright" style={{ height: '40px' }} onClick={() => mergeConstituents()}>
                  <Button.Content visible>
                    <Icon name="i cursor" />
                  </Button.Content>
                  <Button.Content visible>
                    Merge
                  </Button.Content>
                </Button>
              </div>
              {mergeIds.length > 1 && (
                <Modal
                  size="fullscreen"
                  onClose={closeModal}
                  open={isMergeModalOpen}
                  className={themeVersion === 'black' ? 'dark-theme' : ''}
                >
                  <Modal.Header>
                    <span>Merge Constituents</span>
                    <span><Icon name="close" style={{ float: "right", cursor: "pointer" }} onClick={closeModal} /></span>
                  </Modal.Header>
                  <Modal.Content>
                    <Dimmer.Dimmable as="div" dimmed={isLoading}>
                      <Dimmer active={isLoading} inverted={true}>
                        <Loader>Loading</Loader>
                      </Dimmer>
                      <Grid padded>
                        <Grid.Row>
                          <Grid.Column width={3}>
                            <div className='merge-constituent-header'>Fields</div>
                            <List className='merge-constituent-fields'>
                              {mergeFields.map((mergeField: any, mergeFieldIndex) => {
                                return (
                                  <List.Item style={{ backgroundColor: '#F7F7F7', borderBottom: (mergeFieldIndex === mergeFields.length - 1) ? '1px solid #E3E3E3' : 'none' }}><Icon name={mergeField.icon} /> {mergeField.label}</List.Item>
                                );
                              })}
                            </List>
                          </Grid.Column>
                          <Grid.Column width={10}>
                            <Grid columns="equal">
                              <Grid.Row>
                                {mergeRecords.map((mergeRecord: any, mergeIndex: number) => {
                                  masterRecordCaseCount += mergeRecord.caseCount;
                                  masterRecordNotesCount += mergeRecord.notesCount;
                                  return (
                                    <Grid.Column>
                                      <div className='merge-constituent-header'>
                                        <Form.Radio
                                          name={`record-${mergeIndex}`}
                                          id={`record${mergeIndex}`}
                                          label={`Record ${mergeIndex + 1}`}
                                          value={mergeIndex}
                                          checked={selectedMergeIndex === mergeIndex}
                                          onChange={(e, data) => handleSelectAllRadioButtonChange(e, data, mergeIndex)}
                                          style={{ margin: '0rem', fontSize: '1.4rem' }}
                                        />
                                      </div>
                                      <List className='merge-constituent-fields'>
                                        {mergeFields.map((mergeField, mergeFieldIndex) => {
                                          return (
                                            <List.Item style={{ borderBottom: (mergeFieldIndex === mergeFields.length - 1) ? '1px solid #E3E3E3' : 'none' }}>
                                              {(mergeField.value === 'caseCount' || mergeField.value === 'notesCount') && (
                                                <Label color='teal' style={{ fontSize: '1rem' }}>{mergeRecord[mergeField.value]}</Label>
                                              )}
                                              {mergeField.value !== 'caseCount' && mergeField.value !== 'notesCount' && (
                                                <Form.Radio
                                                  name={mergeField.value}
                                                  id={mergeField.value + mergeIndex}
                                                  label={mergeRecord[mergeField.value]}
                                                  value={mergeField.value + mergeIndex}
                                                  checked={selectedMergeFields[mergeField.value] === mergeIndex}
                                                  onChange={(e, data) => handleRadioButtonChange(e, data, mergeIndex, mergeField.value, mergeRecord[mergeField.value])}
                                                  style={{ margin: '0rem', fontSize: '1.4rem' }}
                                                />
                                              )}
                                            </List.Item>
                                          );
                                        })}
                                      </List>
                                    </Grid.Column>
                                  );
                                })}
                              </Grid.Row>
                            </Grid>
                          </Grid.Column>
                          <Grid.Column width={3}>
                            <div className='merge-constituent-master-header'>Master Record</div>
                            <List className='merge-constituent-fields'>
                              {mergeFields.map((mergeField, mergeIndex) => {
                                let borderColor = '#FDFF81';
                                let borderTopColor = '#FDFF81';
                                let backgroundColor = '#FFFFE5';
                                if (selectedMergeFields[mergeField.value] !== undefined || mergeField.value === 'caseCount' || mergeField.value === 'notesCount') {
                                  borderColor = '#66C392';
                                  borderTopColor = '#66C392';
                                  backgroundColor = '#F3FFF4';
                                }
                                if (mergeIndex > 0 && selectedMergeFields[mergeFields[mergeIndex - 1].value] !== undefined) {
                                  borderTopColor = '#66C392';
                                }
                                return (
                                  <List.Item style={{ backgroundColor, borderTop: `1px solid ${borderTopColor}`, borderRight: `1px solid ${borderColor}`, borderBottom: (mergeIndex === mergeFields.length - 1) ? `1px solid ${borderColor}` : 'none', borderLeft: `1px solid ${borderColor}` }}>
                                    {(mergeField.value === 'caseCount') && (
                                      <Label color='teal' style={{ fontSize: '1rem' }}>{masterRecordCaseCount}</Label>
                                    )}
                                    {(mergeField.value === 'notesCount') && (
                                      <Label color='teal' style={{ fontSize: '1rem' }}>{masterRecordNotesCount}</Label>
                                    )}
                                    {mergeField.value !== 'caseCount' && mergeField.value !== 'notesCount' && (
                                      <>
                                        {masterRecord[mergeField.value] !== undefined ? masterRecord[mergeField.value] : 'No data selected'}
                                      </>
                                    )}
                                  </List.Item>
                                );
                              })}
                            </List>
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Dimmer.Dimmable>
                  </Modal.Content>
                  <Modal.Actions>
                    <Grid>
                      <Grid.Column>
                        <Button type="submit" onClick={handleMergeConstituents} className='violet right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem' }}>Merge Selected</Button>
                        <Button type="button" className='second right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem', color: 'white' }} onClick={closeModal}>Cancel</Button>
                      </Grid.Column>
                    </Grid>
                  </Modal.Actions>
                </Modal>
              )}
              <DataGrid<ConstituentDataViewModel>
                apiUrl={`/Constituent/ConstituentDatagrid`}
                downloadURL={`/Constituent/DownloadConstituentDatagrid`}
                initialViewModelURL={`/Constituent/GetConstituentDatagrid`}
                columns={columns}
                rowsPerPage={10}
                defaultSortByField={'lastName'}
                defaultSortOrderDesc={false}
                rowKey={(row: ConstituentDataViewModel) => row.constituentId}
                noDataMessage={'No constituent(s) to display'}
                searchPlaceholder='Search by first name, last name, and email'
                filters={filters}
                setFilters={setFilters}
                setAggregates={setAggregates}
                responseDataModel={responseDataModel}
                setResponseDataModel={setResponseDataModel}
              />
            </div>
          </Sidebar.Pusher>
        </Sidebar.Pushable>
        <button
          className={buttonToggleClass}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => setVisible(!visible)}
          style={{ backgroundColor: '#7152F6' }}
        ></button>
      </section>
    </div>
  );
}

export default Constituents;